import { UnitType } from "../models/ingredient";
import { ParsedIngredient } from "../models/parsed-recipe";
import { normalizeField } from "./textNormalize";

// Recognise an ingredient group heading in any supported form:
//   "# Sauce"  ·  "[Sauce]"  ·  "Sauce:"  ·  "For the sauce"
// Returns the heading text, or null if the line isn't a heading. Shared by the
// bulk-paste importer and the per-section bulk editor so both accept the same.
export function matchIngredientGroup(line: string): string | null {
  const trimmed = line.trim();
  const hash = trimmed.match(/^#+\s*(.+)$/);
  if (hash) return hash[1].trim();
  const bracket = trimmed.match(/^\[(.+)\]$/);
  if (bracket) return bracket[1].trim();
  if (/:$/.test(trimmed)) return trimmed.replace(/:$/, "").trim();
  if (/^(for|para)\s+/i.test(trimmed)) return trimmed;
  return null;
}

// Method-step section heading — a stricter cousin of matchIngredientGroup.
// Method steps are prose blocks, so the bare "for …" rule ("For 5 minutes…") and
// an any-length trailing colon ("…proceed as follows:") are dropped to avoid
// turning real instructions into headings. Only accepts an explicit `# Heading`,
// a `[Heading]`, or a short single-line block ending in a colon ("Béchamel
// sauce:"). Returns the heading text, or null if the block isn't a heading.
export function matchMethodGroup(block: string): string | null {
  const trimmed = block.trim();
  if (trimmed.includes("\n")) return null; // headings are a single line
  const hash = trimmed.match(/^#+\s*(.+)$/);
  if (hash) return hash[1].trim();
  const bracket = trimmed.match(/^\[(.+)\]$/);
  if (bracket) return bracket[1].trim();
  if (/:$/.test(trimmed) && trimmed.length <= 60) {
    return trimmed.replace(/:$/, "").trim();
  }
  return null;
}

// Fraction text (ascii or unicode) -> FractionUnit KEY used by the model. Only
// the six values FractionUnit models can be a `fraction` field.
const FRACTIONS: Record<string, string> = {
  "1/2": "Half",
  "½": "Half",
  "1/4": "Quarter",
  "¼": "Quarter",
  "3/4": "ThreeQuarters",
  "¾": "ThreeQuarters",
  "1/3": "Third",
  "⅓": "Third",
  "2/3": "TwoThirds",
  "⅔": "TwoThirds",
  "1/8": "Eighth",
  "⅛": "Eighth",
};

// Numeric value of every common vulgar-fraction glyph (a superset of the six in
// FRACTIONS). Glyphs outside FractionUnit can't be a `fraction`, so they fold
// into a decimal `quantity` instead — so "⅕ tsp" / "1½ cups" still parse
// structurally rather than dropping to a raw name.
const VULGAR: Record<string, number> = {
  "¼": 1 / 4, "½": 1 / 2, "¾": 3 / 4, "⅓": 1 / 3, "⅔": 2 / 3,
  "⅕": 1 / 5, "⅖": 2 / 5, "⅗": 3 / 5, "⅘": 4 / 5,
  "⅙": 1 / 6, "⅚": 5 / 6, "⅐": 1 / 7, "⅑": 1 / 9, "⅒": 1 / 10,
  "⅛": 1 / 8, "⅜": 3 / 8, "⅝": 5 / 8, "⅞": 7 / 8,
};
const GLYPHS = Object.keys(VULGAR).join("");
// A fraction token: an ascii "a/b" or any single vulgar glyph.
const FRAC = `\\d+\\/\\d+|[${GLYPHS}]`;
// "1½" -> "1 ½": insert a space between a digit and a glued fraction glyph so the
// very common no-space mixed number (typical in JSON-LD) parses like "1 ½".
const GLUED_RE = new RegExp(`(\\d)\\s*([${GLYPHS}])`, "g");
const WHOLE_FRAC_RE = new RegExp(`^(\\d+)\\s+(${FRAC})\\s*`);
const FRAC_ALONE_RE = new RegExp(`^(${FRAC})\\s*`);
// A leading numeric range: "1-2", "1 – 2", "1 to 2" (hyphen/en-dash/em-dash or
// the word "to"; both bounds may be decimals). Kept as a range instead of
// collapsing to the lower bound. The trailing `(\d…)` is what stops "1 tomato"
// from matching on "to" — there's no digit after it, so the branch backtracks.
const RANGE_RE = /^(\d+(?:\.\d+)?)\s*(?:-|–|—|to)\s*(\d+(?:\.\d+)?)\s*/;
// A dual-unit alternate to strip from the start of a name once the primary
// amount+unit are parsed: "125g/4½oz mozzarella" leaves "/4½oz mozzarella",
// "225g/9oz baby tomatoes" leaves "/9oz baby tomatoes", and a compound imperial
// "1.5kg/3lb 5oz chicken" leaves "/3lb 5oz chicken". Each amount+unit group must
// START with a digit or fraction glyph — so repeating it can't chew into a real
// name (words like "chicken" don't start with a digit). Within a group the
// number allows digits, spaces, dots, slashes and glyphs (covering fractions and
// the space GLUED_RE inserts, "4½"→"4 ½"), then 1–4 unit letters.
const DUAL_UNIT_RE = new RegExp(
  `^\\/\\s*(?:[\\d${GLYPHS}][\\d.\\s\\/${GLYPHS}]*[a-zA-Z]{1,4}\\.?\\s*)+`,
);

// Numeric value of a fraction token (glyph or "a/b"), or null if unrecognised.
function fractionValue(tok: string): number | null {
  if (VULGAR[tok] !== undefined) return VULGAR[tok];
  const m = tok.match(/^(\d+)\/(\d+)$/);
  if (m) {
    const v = Number(m[1]) / Number(m[2]);
    return Number.isFinite(v) && v > 0 ? v : null;
  }
  return null;
}

// Trim a computed decimal to 4dp with no trailing zeros ("0.1667", "0.3333").
function fmtDecimal(n: number): string {
  return parseFloat(n.toFixed(4)).toString();
}

// Written unit forms (singular/plural/synonyms) -> canonical enum VALUE + type.
const UNITS: Record<string, { unit: string; type: UnitType }> = {
  g: { unit: "g", type: UnitType.Weight },
  gram: { unit: "g", type: UnitType.Weight },
  grams: { unit: "g", type: UnitType.Weight },
  kg: { unit: "kg", type: UnitType.Weight },
  kilo: { unit: "kg", type: UnitType.Weight },
  kilos: { unit: "kg", type: UnitType.Weight },
  kilogram: { unit: "kg", type: UnitType.Weight },
  kilograms: { unit: "kg", type: UnitType.Weight },
  oz: { unit: "oz", type: UnitType.Weight },
  ounce: { unit: "oz", type: UnitType.Weight },
  ounces: { unit: "oz", type: UnitType.Weight },
  lb: { unit: "lb", type: UnitType.Weight },
  lbs: { unit: "lb", type: UnitType.Weight },
  pound: { unit: "lb", type: UnitType.Weight },
  pounds: { unit: "lb", type: UnitType.Weight },
  ml: { unit: "ml", type: UnitType.Volume },
  milliliter: { unit: "ml", type: UnitType.Volume },
  millilitre: { unit: "ml", type: UnitType.Volume },
  milliliters: { unit: "ml", type: UnitType.Volume },
  millilitres: { unit: "ml", type: UnitType.Volume },
  l: { unit: "L", type: UnitType.Volume },
  liter: { unit: "L", type: UnitType.Volume },
  litre: { unit: "L", type: UnitType.Volume },
  liters: { unit: "L", type: UnitType.Volume },
  litres: { unit: "L", type: UnitType.Volume },
  tsp: { unit: "tsp", type: UnitType.Volume },
  teaspoon: { unit: "tsp", type: UnitType.Volume },
  teaspoons: { unit: "tsp", type: UnitType.Volume },
  tbsp: { unit: "tbsp", type: UnitType.Volume },
  tbs: { unit: "tbsp", type: UnitType.Volume },
  tablespoon: { unit: "tbsp", type: UnitType.Volume },
  tablespoons: { unit: "tbsp", type: UnitType.Volume },
  cup: { unit: "cup", type: UnitType.Volume },
  cups: { unit: "cup", type: UnitType.Volume },
  piece: { unit: "piece", type: UnitType.Count },
  pieces: { unit: "piece", type: UnitType.Count },
  pinch: { unit: "pinch", type: UnitType.Count },
  pinches: { unit: "pinch", type: UnitType.Count },
  handful: { unit: "handful", type: UnitType.Count },
  handfuls: { unit: "handful", type: UnitType.Count },
  clove: { unit: "clove", type: UnitType.Count },
  cloves: { unit: "clove", type: UnitType.Count },
  slice: { unit: "slice", type: UnitType.Count },
  slices: { unit: "slice", type: UnitType.Count },
  sprig: { unit: "sprig", type: UnitType.Count },
  sprigs: { unit: "sprig", type: UnitType.Count },
  whole: { unit: "whole", type: UnitType.Count },
  wholes: { unit: "whole", type: UnitType.Count },
};

// Collapse redundant outer parentheses. Malformed source (e.g. WP Recipe
// Maker sites like RecipeTinEats) wraps notes so you get "((mince))" or
// "((stock cubes), crumbled (Note 1))". A group that *starts* with another "("
// is a redundant wrapper — legitimate nesting ("crusts removed (Note 1)") never
// begins with "((" — so we drop the outer "(" and its matched ")".
//   "((mince))"                        → "(mince)"
//   "((bell pepper), diced)"           → "(bell pepper), diced"
//   "((stock cubes), crumbled (N 1))"  → "(stock cubes), crumbled (N 1)"
function collapseRedundantParens(s: string): string {
  let out = s;
  for (let pass = 0; pass < 20; pass++) {
    const open = out.indexOf("((");
    if (open === -1) break;
    let depth = 0;
    let close = -1;
    for (let i = open; i < out.length; i++) {
      if (out[i] === "(") depth++;
      else if (out[i] === ")" && --depth === 0) {
        close = i;
        break;
      }
    }
    if (close === -1) break; // unbalanced — leave it alone
    out = out.slice(0, open) + out.slice(open + 1, close) + out.slice(close + 1);
  }
  return out;
}

// Tidy an ingredient name: strip leading connectives/"of", drop a dual-unit
// leftover ("14 oz / 400 g beef" → the "/ 400 g" part), and fix common source
// artifacts (doubled parens, "(, ", spaces inside parens). Best-effort — messy
// source data can still leave the odd stray paren.
function cleanName(s: string): string {
  const out = s
    .replace(/^[\s,.\-–]+/, "") // leading separators
    .replace(/^of\s+/i, "") // "of flour" → "flour"
    .replace(DUAL_UNIT_RE, "") // "/ 400 g" / "/4½oz" dual-unit leftover
    .replace(/\(\s*,\s*/g, "(") // "(, x" → "(x"
    .replace(/\(\s+/g, "(") // "( x" → "(x"
    .replace(/\s+\)/g, ")") // "x )" → "x)"
    .replace(/\(\s*\)/g, ""); // drop empty "()"
  return collapseRedundantParens(out).replace(/\s{2,}/g, " ").trim();
}

// Match a leading unit (incl. the two-word "fl oz"), returning the canonical
// value/type and the remaining string, or just the remainder if none matched.
function matchUnit(s: string): { unit?: string; unitType?: UnitType; remainder: string } {
  const floz = s.match(/^fl\.?\s*oz\.?/i);
  if (floz) {
    return { unit: "fl oz", unitType: UnitType.Volume, remainder: s.slice(floz[0].length) };
  }
  const word = s.match(/^([a-zA-Z]+)\.?/);
  if (word) {
    const found = UNITS[word[1].toLowerCase()];
    if (found) {
      return { unit: found.unit, unitType: found.type, remainder: s.slice(word[0].length) };
    }
  }
  return { remainder: s };
}

// Best-effort parse of a single ingredient line into structured fields. Any line
// that doesn't start with a quantity/fraction is returned as a raw name.
export function parseIngredientLine(raw: string): ParsedIngredient {
  // Clean paste artifacts (nbsp, zero-width chars) so tokenising and the stored
  // name are tidy, whether the line came from Quick Paste or a single typed row.
  const line = normalizeField(raw);
  if (!line) return { name: "" };

  // Normalise glued mixed numbers ("1½" -> "1 ½") before matching.
  let rest = line.replace(GLUED_RE, "$1 $2");
  let quantity: string | undefined;
  let fraction: string | undefined;

  // "2 1/2" or "1 ½" (whole + fraction). Supported glyphs → a fraction field;
  // others (⅕, ⅙…) fold the fraction into a decimal quantity.
  let m = rest.match(WHOLE_FRAC_RE);
  if (m) {
    if (FRACTIONS[m[2]]) {
      quantity = m[1];
      fraction = FRACTIONS[m[2]];
    } else {
      const v = fractionValue(m[2]);
      if (v === null) return { name: cleanName(line) };
      quantity = fmtDecimal(Number(m[1]) + v);
    }
    rest = rest.slice(m[0].length);
  } else if ((m = rest.match(FRAC_ALONE_RE))) {
    // fraction alone
    if (FRACTIONS[m[1]]) {
      fraction = FRACTIONS[m[1]];
    } else {
      const v = fractionValue(m[1]);
      if (v === null) return { name: cleanName(line) };
      quantity = fmtDecimal(v);
    }
    rest = rest.slice(m[0].length);
  } else if ((m = rest.match(RANGE_RE))) {
    // numeric range, e.g. "1-2" — stored canonically as "lo-hi"
    quantity = `${m[1]}-${m[2]}`;
    rest = rest.slice(m[0].length);
  } else if ((m = rest.match(/^(\d+(?:\.\d+)?)\s*/))) {
    // whole or decimal
    quantity = m[1];
    rest = rest.slice(m[0].length);
  }

  // No leading amount → treat the whole line as the name.
  if (quantity === undefined && fraction === undefined) {
    return { name: cleanName(line) };
  }

  const { unit, unitType, remainder } = matchUnit(rest);
  return {
    name: cleanName(remainder),
    quantity: quantity ?? null,
    fraction: fraction ?? null,
    unit: unit ?? null,
    unitType: unitType ?? null,
  };
}

// "main | substitute" — anything after the first pipe parses as the ingredient's
// substitute. One source of truth for the pipe syntax, shared by the bulk-edit
// boxes, quick paste, the AI scan (whose prompt emits it), and the URL importer.
// A pipe with nothing parseable after it falls back to the main ingredient alone.
export function parseIngredientLineWithSubstitute(raw: string): ParsedIngredient {
  const idx = raw.indexOf("|");
  if (idx === -1) return parseIngredientLine(raw);
  const main = parseIngredientLine(raw.slice(0, idx).trim());
  const sub = parseIngredientLine(raw.slice(idx + 1).trim());
  if (!sub.name) return main;
  return {
    ...main,
    substitute: {
      name: sub.name,
      quantity: sub.quantity ?? null,
      fraction: sub.fraction ?? null,
      unitType: sub.unitType ?? null,
      unit: sub.unit ?? null,
      other: sub.other ?? null,
    },
  };
}
